/**
 * De bewegingen die het origineel had.
 *
 * De cd bevat er de beelden al voor: elke dobbelworp heeft zijn eigen reeks van twintig
 * plaatjes, elke grondstof een reeks van veertien waarin het schaap of het blok
 * wegdwarrelt. Deze module speelt die reeksen af; hij bepaalt niets over het spel.
 *
 * Alles hangt in een laag *boven* de omlijsting, want een kaart die groot in beeld komt
 * hoort over de statusbalken heen te liggen, niet eronder.
 *
 * Wie "verminderde beweging" heeft aangezet in zijn systeem krijgt overal het eindbeeld
 * zonder tussenstappen. De uitkomst blijft dus zichtbaar, alleen zonder de rit ernaartoe.
 */
import { cardById } from '@catan/contracts';
import { Container, Sprite, Texture } from 'pixi.js';
import { asset, assetGroup, hasAsset, playSound, speelVerhaal } from './assets.js';
import { kostenRijen } from './kosten.js';
import { negenStuk, tekst, RASTER_BREED, RASTER_HOOG } from './skin.js';
import { FX, kaartMuziek } from './klanken.js';

/**
 * Heeft deze bezoeker beweging uitgezet?
 *
 * Dan slaan de animaties hun bewegende deel over. Dat geldt ook voor de wachttijden
 * eromheen — zie `main.ts` — want wie geen beweging wil, wil ook niet vier seconden naar
 * een stilstaande kaart kijken.
 */
export const stilleModus = (): boolean =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const wacht = (ms: number): Promise<void> =>
  new Promise((klaar) => {
    window.setTimeout(klaar, ms);
  });

/** De namen van een animatiereeks op volgorde. */
function frames(groep: string): string[] {
  return Object.keys(assetGroup(groep)).sort((a, b) => Number(a) - Number(b));
}

/**
 * Speelt een reeks af op één sprite.
 *
 * Levert een belofte op die pas rond is als het laatste beeld heeft gestaan, zodat de
 * aanroeper er iets achteraan kan zetten.
 */
async function speelReeks(sprite: Sprite, groep: string, msPerBeeld: number): Promise<void> {
  const namen = frames(groep);
  if (namen.length === 0) return;
  const beelden = assetGroup(groep);
  if (stilleModus()) {
    sprite.texture = Texture.from(beelden[namen[namen.length - 1]!]!.src);
    return;
  }
  for (const naam of namen) {
    sprite.texture = Texture.from(beelden[naam]!.src);
    await wacht(msPerBeeld);
  }
}

/** Laat iets in een aantal stappen verdwijnen. */
async function vervaag(doel: Container, ms = 260): Promise<void> {
  if (stilleModus()) {
    doel.destroy({ children: true });
    return;
  }
  const stappen = Math.max(1, Math.round(ms / 30));
  for (let i = 0; i < stappen; i++) {
    doel.alpha = 1 - (i + 1) / stappen;
    await wacht(30);
  }
  doel.destroy({ children: true });
}

// ------------------------------------------------------------------ dobbelen

/** De Nederlandse naam van een gebeurtenisvlak, zoals de balk hem noemt. */
const GEBEURTENIS_TEKST: Readonly<Record<string, string>> = {
  'goed-jaar': 'Feestmaal — goed jaar',
  handelsvoordeel: 'Handelsvoordeel',
  roofoverval: 'Roofoverval',
  riddertoernooi: 'Riddertoernooi',
  gebeurteniskaart: 'Gebeurteniskaart',
};

/**
 * De worp: beide stenen rollen, blijven liggen, en je krijgt de tijd om ze te lezen.
 *
 * Links de opbrengstdobbelsteen met zijn cijfer, rechts de gebeurtenisdobbelsteen met
 * zijn icoon — die tweede bepaalt wat er dit rondje gebeurt, dus die moet je echt kunnen
 * zien. Vandaar dat het eindbeeld blijft staan met de naam eronder in plaats van meteen
 * weg te vallen.
 */
export async function dobbelworp(
  laag: Container,
  opbrengst: number,
  gebeurtenis: string,
): Promise<void> {
  const doos = new Container();
  doos.x = RASTER_BREED / 2 - 140;
  doos.y = RASTER_HOOG / 2 - 110;
  laag.addChild(doos);

  doos.addChild(negenStuk('MENU_092', 280, 190, 14));

  const label = tekst('', 15);
  label.anchor.set(0.5, 0);
  label.x = 140;
  label.y = 138;
  doos.addChild(label);

  // De plaatjes zijn klein: de rolbeelden 45×45, het eindvlak 32×33. Op 96 px werd het
  // eindvlak dus drie keer uitvergroot en zag je de blokjes. Op 64 blijft het formaat
  // leesbaar maar valt het uitvergroten nauwelijks meer op.
  const STEEN = 64;
  const steen = (midden: number): Sprite => {
    const s = new Sprite(Texture.from(asset('dobbelstenen', 'opbrengst-1').src));
    s.setSize(STEEN, STEEN);
    s.x = midden - STEEN / 2;
    s.y = 72 - STEEN / 2;
    doos.addChild(s);
    return s;
  };
  const links = steen(76);
  const rechts = steen(204);

  // Hier klonk `FX_000` — het klikje van het menu, over de dobbelworp heen. De worp heeft
  // zijn eigen klank (`FX_016`/`FX_017`) en die klinkt al bij de zet; een menuklik erbij
  // is de blunder uit kennisbank 06, en die is nu ook op deze plek weg.

  // Trager dan een gewone animatie: je moet de steen zien tollen, niet zien flitsen.
  await Promise.all([
    speelReeks(links, `anim-worp-${opbrengst}`, 70),
    speelReeks(rechts, `anim-worp-${gebeurtenis}`, 70),
  ]);

  // Het eindvlak als los, scherp plaatje — de laatste animatiestap is een tussenstand.
  if (hasAsset('dobbelstenen', `opbrengst-${opbrengst}`)) {
    links.texture = Texture.from(asset('dobbelstenen', `opbrengst-${opbrengst}`).src);
  }
  if (hasAsset('dobbelstenen', `gebeurtenis-${gebeurtenis}`)) {
    rechts.texture = Texture.from(asset('dobbelstenen', `gebeurtenis-${gebeurtenis}`).src);
  }
  label.text = `${opbrengst}  ·  ${GEBEURTENIS_TEKST[gebeurtenis] ?? gebeurtenis}`;

  await wacht(stilleModus() ? 400 : 1500);
  await vervaag(doos);
}

/**
 * De worp van een aanvalskaart: één dobbelsteen, even nadrukkelijk als aan het begin van je
 * beurt.
 *
 * De Zwarte ridder, de Vuurduivel en de Rooftocht worden met één steen beslecht (handboek
 * p. 11). Die worp bestond wel — de kern rolde hem netjes — maar je zag hem niet: het scherm
 * sprong naar de uitkomst. Uit de eerste live test: "de aanvalsworp van de Zwarte ridder is
 * onzichtbaar".
 *
 * **Zelfde kader, zelfde steen, zelfde tijd als `dobbelworp`.** Dat is met opzet: een worp
 * die kleiner of korter in beeld staat leest als iets bijkomstigs, terwijl hij de hele kaart
 * beslist. Alleen het aantal stenen verschilt — de tweede is de gebeurtenisdobbelsteen, en
 * die hoort hier niet: hij zou suggereren dat er óók een gebeurtenis gerold werd.
 *
 * De naam van de aanval staat erbij, want anders zie je een steen rollen zonder te weten
 * waarvoor.
 */
export async function aanvalsWorp(
  laag: Container,
  worp: number,
  uitleg: string,
  aanval?: string,
): Promise<void> {
  const doos = new Container();
  // Exact de maat en plek van de gewone worp.
  doos.x = RASTER_BREED / 2 - 140;
  doos.y = RASTER_HOOG / 2 - 110;
  laag.addChild(doos);

  doos.addChild(negenStuk('MENU_092', 280, 190, 14));

  // De aanval bovenin, zodat je meteen ziet waarvoor er gerold wordt.
  if (aanval !== undefined && aanval !== '') {
    const kop = tekst(aanval, 16);
    kop.anchor.set(0.5, 0);
    kop.x = 140;
    kop.y = 14;
    doos.addChild(kop);
  }

  const label = tekst('', 15);
  label.anchor.set(0.5, 0);
  label.x = 140;
  label.y = 138;
  doos.addChild(label);

  const STEEN = 64;
  const steen = new Sprite(Texture.from(asset('dobbelstenen', 'opbrengst-1').src));
  steen.setSize(STEEN, STEEN);
  steen.x = 140 - STEEN / 2;
  steen.y = 78 - STEEN / 2;
  doos.addChild(steen);

  await speelReeks(steen, `anim-worp-${worp}`, 70);

  // Het eindvlak als scherp plaatje; de laatste animatiestap is een tussenstand.
  if (hasAsset('dobbelstenen', `opbrengst-${worp}`)) {
    steen.texture = Texture.from(asset('dobbelstenen', `opbrengst-${worp}`).src);
  }
  label.text = uitleg === '' ? String(worp) : `${worp}  ·  ${uitleg}`;

  await wacht(stilleModus() ? 400 : 1500);
  await vervaag(doos);
}

// ------------------------------------------------------------------ kaarten

/**
 * Wat er klinkt bij een kaart zonder eigen muziekstuk: het korte "kaart neergelegd".
 *
 * Welke kaart wél een eigen stuk heeft staat in `klanken.ts`; daar staat ook waarom die
 * tabel korter is dan het aantal stukken op de schijf.
 */
const KAART_KLANK = FX.kaartNeergelegd;

/**
 * Een gespeelde kaart komt groot in het midden, met zijn geluid.
 *
 * Zo zag je in het origineel wat je tegenstander deed: de kaart schuift naar het midden,
 * blijft even liggen zodat je hem kunt lezen, en verdwijnt dan.
 */
export async function kaartInBeeld(
  laag: Container,
  cardId: number,
  regeltekst?: string,
): Promise<void> {
  if (!hasAsset('kaarten', String(cardId))) return;

  // Het origineel toont de kaart op ware grootte — 400×400, de hele plaat. De kleur van
  // het tekstbalkje zit al in de plaat: groen bij dorp én stad, roze bij stad-alleen,
  // geel bij actiekaarten, blauw bij gebeurtenissen.
  const maat = 400;
  const doos = new Container();
  laag.addChild(doos);

  const plaat = new Sprite(Texture.from(asset('kaarten', String(cardId)).src));
  plaat.setSize(maat, maat);
  doos.addChild(plaat);

  // De kostenstrook, uitvergroot mee. De plaat is een kale illustratie zonder
  // kostensymbolen, dus wat de kleine kaart linksboven toont hoort hier ook te staan —
  // rij per grondstofsoort, het aantal naast elkaar, in de gedrukte volgorde.
  for (const [rij, { soort, aantal }] of kostenRijen(cardById(cardId)?.cost).entries()) {
    if (!hasAsset('symbolen', soort)) continue;
    for (let k = 0; k < aantal; k++) {
      const icoon = new Sprite(Texture.from(asset('symbolen', soort).src));
      icoon.setSize(30, 30);
      icoon.x = 14 + k * 32;
      icoon.y = 14 + rij * 32;
      doos.addChild(icoon);
    }
  }

  // De regeltekst van Teuber hoort in het gekleurde balkje onderaan de kaart — het
  // origineel typt hem daar letterlijk in ("Pakhuis - 1 handelspunt. De grondst…", zie
  // `catan-ref/shots/mij/grote-kaart-als-je-gebouw-bouwt.png`). Dat balkje zit al in de
  // plaat gebakken, in de kleur die bij de kaartsoort hoort; we tekenen er dus geen
  // eigen vak overheen maar zetten alleen de tekst erin.
  if (regeltekst !== undefined && regeltekst !== '') {
    // Opgemeten op de kaartplaten: het vak loopt van 48 tot 352 breed en 288 tot 368 hoog.
    const vak = { x: 52, y: 292, breed: 296, hoog: 72 };
    const label = tekst(regeltekst, 14, 0x14260a);
    label.style.stroke = { color: 0x000000, width: 0 };
    label.style.wordWrap = true;
    label.style.wordWrapWidth = vak.breed;
    // Lange regeltekst krimpt liever dan dat hij buiten het balkje valt.
    if (label.height > vak.hoog) label.scale.set(vak.hoog / label.height);
    label.x = vak.x;
    label.y = vak.y;
    doos.addChild(label);
  }

  doos.pivot.set(maat / 2, maat / 2);
  doos.x = RASTER_BREED / 2;
  doos.y = RASTER_HOOG / 2;

  // De muziek van de kaart, en anders het korte effect. Het stuk gaat op de verhaalbaan en
  // blijft dus doorspelen als de kaart weg is — het duurt vier tot tweeëntwintig seconden
  // en de kaart staat er ruim vier. Wie het afkapte hoorde van Conflict alleen de aanloop.
  const muziek = kaartMuziek(cardId);
  if (muziek === undefined) playSound('effecten', KAART_KLANK);
  else speelVerhaal('kaarten', muziek);

  // Van klein naar groot: de kaart komt naar je toe in plaats van er ineens te staan.
  if (!stilleModus()) {
    const stappen = 12;
    for (let i = 0; i < stappen; i++) {
      const t = (i + 1) / stappen;
      doos.scale.set(0.4 + 0.6 * t);
      doos.alpha = t;
      await wacht(22);
    }
  }
  doos.scale.set(1);
  doos.alpha = 1;

  // Ruim drie seconden stil laten liggen: met de oude anderhalve seconde was het lezen,
  // de banner erna en de volgende zet één aaneengesloten flits. Met de aanloop en het
  // vervagen erbij staat de kaart er ruim vier seconden — dat is `KAART_MS` in main.ts.
  await wacht(stilleModus() ? 500 : 3600);
  await vervaag(doos, 300);
}

// -------------------------------------------------------------- grondstoffen

/** Van grondstofnaam naar de animatiereeks op de schijf. */
const GRONDSTOF_REEKS: Readonly<Record<string, string>> = {
  wol: 'anim-grondstof-wol',
  hout: 'anim-grondstof-hout',
  erts: 'anim-grondstof-erts',
  goud: 'anim-grondstof-goud',
  graan: 'anim-grondstof-graan',
  klei: 'anim-grondstof-leem',
  tovenarij: 'anim-grondstof-tovenarij',
};

/**
 * Een grondstof die je afrekent, vliegt weg.
 *
 * Je klikt het schaap aan, ziet het opstijgen en wegdwarrelen, en dan pas zakt je
 * voorraad. Dat is het verschil tussen "het getal is veranderd" en "ik heb betaald".
 */
export async function grondstofWegvliegen(
  laag: Container,
  grondstof: string,
  vanX: number,
  vanY: number,
): Promise<void> {
  const groep = GRONDSTOF_REEKS[grondstof];
  if (groep === undefined || frames(groep).length === 0) return;

  const sprite = new Sprite(Texture.from(asset(groep, frames(groep)[0]!).src));
  sprite.setSize(50, 50);
  sprite.anchor.set(0.5);
  sprite.x = vanX;
  sprite.y = vanY;
  laag.addChild(sprite);

  // De grondstofklank. Hier stond `FX_003`, en dat is "kaarten geschud" — een geluid dat
  // met grondstoffen niets te maken heeft. De speler noemde `FX_007` "grondstof erbij";
  // dat is het geluid van een grondstof die verplaatst, of hij nu komt of gaat.
  playSound('effecten', FX.grondstofErbij);

  // De reeks speelt af terwijl het beeld omhoog drijft en kleiner wordt.
  const namen = frames(groep);
  const beelden = assetGroup(groep);
  if (stilleModus()) {
    sprite.destroy();
    return;
  }
  for (const [i, naam] of namen.entries()) {
    sprite.texture = Texture.from(beelden[naam]!.src);
    const t = (i + 1) / namen.length;
    sprite.y = vanY - 46 * t;
    sprite.alpha = 1 - t * 0.85;
    sprite.scale.set((1 - t * 0.35) * (50 / sprite.texture.width));
    await wacht(45);
  }
  sprite.destroy();
}

/**
 * Een grondstof die van het ene vorstendom naar het andere gaat.
 *
 * Bij een geslaagde ruil zie je in het origineel wát er van hand wisselt: de blokjes
 * gaan één voor één over. Zonder die beweging veranderen er alleen twee getallen en
 * lijkt het alsof je tegenstander er zomaar iets bij tovert.
 */
export async function grondstofVliegtNaar(
  laag: Container,
  grondstof: string,
  van: { x: number; y: number },
  naar: { x: number; y: number },
): Promise<void> {
  const groep = GRONDSTOF_REEKS[grondstof];
  if (groep === undefined || frames(groep).length === 0) return;

  const namen = frames(groep);
  const beelden = assetGroup(groep);
  const sprite = new Sprite(Texture.from(asset(groep, namen[0]!).src));
  sprite.setSize(44, 44);
  sprite.anchor.set(0.5);
  sprite.x = van.x;
  sprite.y = van.y;
  laag.addChild(sprite);

  playSound('effecten', FX.grondstofErbij);
  if (stilleModus()) {
    sprite.destroy();
    return;
  }

  const stappen = 18;
  for (let i = 0; i < stappen; i++) {
    const t = (i + 1) / stappen;
    sprite.texture = Texture.from(beelden[namen[i % namen.length]!]!.src);
    sprite.x = van.x + (naar.x - van.x) * t;
    // Een lichte boog: hij gaat er niet kaarsrecht overheen.
    sprite.y = van.y + (naar.y - van.y) * t - Math.sin(t * Math.PI) * 40;
    sprite.scale.set(44 / sprite.texture.width);
    await wacht(35);
  }
  sprite.destroy();
}
